import React, { useMemo } from 'react';
import { CalendarDays, X } from 'lucide-react';

/**
 * Release-anchored scheduling control (v258.3).
 *
 * Lets the user pin a New Development item or Integration to a Public
 * Cloud release date + a number of delivery weeks. The component
 * resolves the chosen release into `planner_start_week` / `planner_weeks`
 * relative to the project start date so the item finishes ON the
 * release date.
 *
 * Pure controlled component — parent owns the four persisted fields
 * (`anchor_release_id`, `anchor_lead_weeks`, `planner_start_week`,
 * `planner_weeks`) and re-renders this whenever they change.
 */
export default function ReleaseAnchorPicker({
  releases = [],
  projectStartDate,    // ISO 'YYYY-MM-DD' — null when the project has no resolved start
  anchorReleaseId,     // currently selected release id (null when free-form scheduling)
  anchorLeadWeeks,     // user-entered delivery weeks
  onAnchor,            // ({ release_id, lead_weeks, start_week, weeks }) ⇒ void
  onClear,             // () ⇒ void
  testidPrefix = 'release-anchor',
}) {
  // Sort upcoming releases first; show release date in the label so the
  // user doesn't have to leave the row to check the calendar.
  const sorted = useMemo(() => (
    [...releases]
      .filter((r) => r && r.id && r.release_date && r.label)
      .sort((a, b) => (a.release_date || '').localeCompare(b.release_date || ''))
  ), [releases]);

  const selected = useMemo(() => sorted.find((r) => r.id === anchorReleaseId) || null, [sorted, anchorReleaseId]);

  // Project week of the chosen release date.
  const releaseWeek = useMemo(() => {
    if (!selected || !projectStartDate) return null;
    const s = new Date(projectStartDate + 'T00:00:00Z');
    const r = new Date(selected.release_date + 'T00:00:00Z');
    if (Number.isNaN(s.getTime()) || Number.isNaN(r.getTime())) return null;
    const days = Math.floor((r.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    if (days < 0) return null;
    return Math.floor(days / 7) + 1;  // 1-indexed project week
  }, [selected, projectStartDate]);

  // Actual calendar start date (v258.5) — surfaced under the picker once
  // both a release and a lead-weeks value are set, so the PM can confirm
  // the absolute date the work needs to begin without leaving the row.
  const computedStartDate = useMemo(() => {
    const lw = parseInt(anchorLeadWeeks, 10);
    if (!selected || !lw || lw < 1 || !projectStartDate) return null;
    const r = new Date(selected.release_date + 'T00:00:00Z');
    if (Number.isNaN(r.getTime())) return null;
    // Start = release_date − (lead_weeks − 1) × 7 days.
    // Example: release 2027-09-15 with 4 lead weeks → start 2027-08-25
    // (so the 4-week window finishes ON the release date).
    const startMs = r.getTime() - (lw - 1) * 7 * 24 * 60 * 60 * 1000;
    const startDt = new Date(startMs);
    // Clamp to project start if the math underruns (start before W1).
    const projStart = new Date(projectStartDate + 'T00:00:00Z');
    return startDt < projStart ? projStart : startDt;
  }, [selected, anchorLeadWeeks, projectStartDate]);

  const fmtDate = (d) => d
    ? d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  const recompute = (releaseId, leadWeeks) => {
    const lw = parseInt(leadWeeks, 10);
    const rel = sorted.find((r) => r.id === releaseId);
    if (!rel || !lw || lw < 1 || !projectStartDate) {
      onAnchor({ release_id: releaseId || null, lead_weeks: Number.isFinite(lw) ? lw : null, start_week: null, weeks: Number.isFinite(lw) ? lw : null });
      return;
    }
    const s = new Date(projectStartDate + 'T00:00:00Z');
    const r = new Date(rel.release_date + 'T00:00:00Z');
    const days = Math.floor((r.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    const relWeek = Math.max(1, Math.floor(days / 7) + 1);
    const startWeek = Math.max(1, relWeek - lw + 1);
    onAnchor({ release_id: releaseId, lead_weeks: lw, start_week: startWeek, weeks: lw });
  };

  return (
    <div className="bg-cyan-50/40 border border-cyan-200/60 rounded-md p-2.5 space-y-2" data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-cyan-800">
          <CalendarDays className="w-3.5 h-3.5" />
          Anchor to Public Cloud Release
        </div>
        {selected && (
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] text-slate-500 hover:text-rose-600 inline-flex items-center gap-1"
            data-testid={`${testidPrefix}-clear`}
            title="Clear release anchor"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select
          value={anchorReleaseId || ''}
          onChange={(e) => recompute(e.target.value || null, anchorLeadWeeks)}
          className="h-8 rounded border border-slate-200 bg-white px-2 text-xs"
          data-testid={`${testidPrefix}-release-select`}
        >
          <option value="">— Pick a release —</option>
          {sorted.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label} · {r.release_date}
            </option>
          ))}
        </select>
        <input
          type="number"
          min="1"
          placeholder="Delivery weeks"
          value={anchorLeadWeeks || ''}
          onChange={(e) => recompute(anchorReleaseId, e.target.value)}
          className="h-8 rounded border border-slate-200 bg-white px-2 text-xs"
          data-testid={`${testidPrefix}-lead-weeks`}
        />
      </div>
      {selected && anchorLeadWeeks && projectStartDate && releaseWeek && (
        <div className="text-[10px] text-cyan-800/80 space-y-0.5" data-testid={`${testidPrefix}-summary`}>
          <div>
            {anchorLeadWeeks}w window ending on <strong>{selected.label}</strong> (project W{releaseWeek}).
          </div>
          {computedStartDate && (
            <div data-testid={`${testidPrefix}-start-date`}>
              <span className="text-cyan-900">▶ Starts</span> <strong>{fmtDate(computedStartDate)}</strong>
              {' '}(project W{Math.max(1, releaseWeek - anchorLeadWeeks + 1)})
              {' → '}
              <span className="text-cyan-900">▶ Ends</span> <strong>{fmtDate(new Date(selected.release_date + 'T00:00:00Z'))}</strong>
            </div>
          )}
        </div>
      )}
      {selected && !projectStartDate && (
        <div className="text-[10px] text-amber-700" data-testid={`${testidPrefix}-no-start`}>
          Set a project start date in Project Conditions to resolve the release week.
        </div>
      )}
    </div>
  );
}
